import { Box } from '@mantine/core';
import { forwardRef, useState, useEffect, useRef } from 'react';
import { useReducedMotion } from '../hooks';

export interface CrossFadeProps {
  children: React.ReactNode;
  transitionKey: string | number;
  duration?: number;
  timingFunction?: string;
}

/**
 * Cross-fades between content changes
 * Both old and new content are visible during transition
 */
const CrossFade = forwardRef<HTMLDivElement, CrossFadeProps>(
  (
    {
      children,
      transitionKey,
      duration = 300,
      timingFunction = 'ease-in-out',
    },
    ref
  ) => {
    const reducedMotion = useReducedMotion();
    const [items, setItems] = useState([{ key: transitionKey, content: children }]);
    const actualDuration = reducedMotion ? 0 : duration;

    useEffect(() => {
      const currentItem = items[items.length - 1];

      if (transitionKey !== currentItem?.key) {
        // Add new content
        setItems((prev) => [...prev, { key: transitionKey, content: children }]);

        // Remove old content after animation
        const timer = setTimeout(() => {
          setItems((prev) => prev.slice(-1));
        }, actualDuration);

        return () => clearTimeout(timer);
      } else {
        // Update current content without animation
        setItems([{ key: transitionKey, content: children }]);
      }
    }, [transitionKey, children, actualDuration]);

    return (
      <Box ref={ref} style={{ position: 'relative' }}>
        {items.map((item, index) => {
          const isLatest = index === items.length - 1;
          const isEntering = items.length > 1 && isLatest;

          return (
            <Box
              key={item.key}
              style={{
                position: isLatest ? 'relative' : 'absolute',
                top: 0,
                left: 0,
                right: 0,
                opacity: isEntering ? 1 : items.length > 1 ? 0 : 1,
                transition: reducedMotion ? 'none' : `opacity ${actualDuration}ms ${timingFunction}`,
                zIndex: isLatest ? 1 : 0,
              }}
            >
              {item.content}
            </Box>
          );
        })}
      </Box>
    );
  }
);

CrossFade.displayName = 'CrossFade';

export default CrossFade;
